import type { ElementType, ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemAs, DesignSystemElementProps, DesignSystemRef } from "./contracts/dom";

const CONTENT_BLOCK_WIDTH_CLASSES = {
  compactPreview: "max-w-compact-preview",
  flow: "max-w-content-flow",
  constrained: "max-w-content-constrained",
  full: "max-w-content-full",
} as const;

type ContentBlockElement = "div" | "main" | "section" | "article" | "aside" | "nav";
export type ContentBlockWidth = keyof typeof CONTENT_BLOCK_WIDTH_CLASSES;
export type ContentBlockAlign = "left" | "center";

export type ContentBlockProps<TElement extends ContentBlockElement = "div"> = {
  children: ReactNode;
  width?: ContentBlockWidth;
  align?: ContentBlockAlign;
} & DesignSystemAs<TElement> &
  DesignSystemElementProps<TElement> &
  DesignSystemRef<HTMLElementTagNameMap[TElement]>;

/** A full-width content region with a named maximum width, optionally centered. */
export function ContentBlock<TElement extends ContentBlockElement = "div">({
  as,
  children,
  ref,
  width = "full",
  align = "left",
  ...nativeProps
}: ContentBlockProps<TElement>): ReactNode {
  const Element = (as ?? "div") as ElementType;

  return (
    <Element
      {...nativeProps}
      ref={ref}
      className={twJoin("w-full", CONTENT_BLOCK_WIDTH_CLASSES[width], align === "center" && "mx-auto")}
    >
      {children}
    </Element>
  );
}
